import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FormSection } from "@/components/entity-page/FormSection";
import { updateFormOption, deleteFormOption } from "@/lib/api";
import type { FormOption } from "@/types";

interface EditModelFormProps {
  model: FormOption;
  showDeleteConfirm: boolean;
  onCancelDelete: () => void;
  onDeleted: () => void;
  onSaved: () => void;
}

const metadataSchema = z.string().refine(
  (val) => {
    if (!val) return true;
    try {
      JSON.parse(val);
      return true;
    } catch {
      return false;
    }
  },
  { message: "Metadata must be valid JSON" }
);

export default function EditModelForm({
  model,
  showDeleteConfirm,
  onCancelDelete,
  onDeleted,
  onSaved,
}: EditModelFormProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const initialMetadata =
    typeof model.metadata === "string"
      ? model.metadata
      : model.metadata
      ? JSON.stringify(model.metadata, null, 2)
      : "";

  const form = useForm({
    defaultValues: {
      label: model.label,
      model: model.model || "",
      description: model.description || "",
      order: model.order ?? 0,
      metadata: initialMetadata,
    },
    onSubmit: async ({ value }) => {
      const updates = {
        ...(value.label !== model.label && { label: value.label }),
        ...(value.model !== (model.model || "") && { model: value.model }),
        ...(value.description !== (model.description || "") && {
          description: value.description,
        }),
        ...(value.order !== (model.order ?? 0) && { order: value.order }),
        ...(value.metadata !== initialMetadata && { metadata: value.metadata }),
      };

      if (Object.keys(updates).length === 0) {
        toast.info("No changes to save");
        return;
      }

      try {
        const result = await updateFormOption("models", model.id, updates);

        if (result.success) {
          toast.success("Model updated successfully");
          onSaved();
        } else {
          toast.error("Failed to update model", {
            description: result.error,
          });
        }
      } catch (error) {
        console.error("Error updating model:", error);
        toast.error("An error occurred", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteFormOption("models", model.id);

      if (result.success) {
        toast.success("Model deleted successfully");
        onDeleted();
      } else {
        toast.error("Failed to delete model", {
          description: result.error,
        });
        setIsDeleting(false);
      }
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error("An error occurred", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
      setIsDeleting(false);
    }
  };

  if (showDeleteConfirm) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <h3 className="font-semibold text-destructive mb-2">Delete Model?</h3>
          <p className="text-sm text-muted-foreground">
            Agents currently set to this model keep their existing modelId value, but this model
            will no longer appear as a selectable option when creating or editing an agent.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Model"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="space-y-6"
    >
      <FormSection
        title="Model Details"
        description="A model is what Agent.modelId references - it determines which LLM an agent uses."
      >
        <div className="space-y-2">
          <Label className="text-foreground">Value</Label>
          <Input value={model.value} disabled className="text-foreground" />
          <p className="text-xs text-muted-foreground">The value can't be changed after creation.</p>
        </div>

        <form.Field
          name="label"
          validators={{
            onChange: z.string().min(1, "Label is required"),
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="label" className="text-foreground">
                Label
              </Label>
              <Input
                id="label"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
              />
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="model"
          validators={{
            onChange: z.string().min(1, "Model is required"),
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="model" className="text-foreground">
                Model
              </Label>
              <Input
                id="model"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
                placeholder="e.g., gpt-4o, claude-sonnet-4-5"
              />
              <p className="text-xs text-muted-foreground">
                The exact LLM model identifier sent to the provider API - distinct from the Label
                above, which is just what admins see in dropdowns.
              </p>
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="description">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">
                Description
              </Label>
              <Textarea
                id="description"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
                placeholder="What this model is for"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="order">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="order" className="text-foreground">
                Display Order
              </Label>
              <Input
                id="order"
                type="number"
                value={field.state.value}
                onChange={(e) => field.handleChange(Number(e.target.value))}
                onBlur={field.handleBlur}
                className="text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                Controls the display order of this option in select dropdowns elsewhere in the
                app (e.g. the agent form). Lower numbers appear first.
              </p>
            </div>
          )}
        </form.Field>

        <form.Field
          name="metadata"
          validators={{
            onChange: metadataSchema,
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="metadata" className="text-foreground">
                Metadata (JSON)
              </Label>
              <Textarea
                id="metadata"
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder='{"parameters": { ... }, "modelConfig": { ... }}'
                className="font-mono text-sm"
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                Optional JSON object for model-specific configuration, e.g. parameters
                (temperature, max tokens, etc.) and modelConfig.
              </p>
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </FormSection>

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={form.state.isSubmitting}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}
